import React from "react";
import { useNavigate } from "react-router";

import * as types from "types";
import styles from "styles/chat.module.scss";
import {
  loadLastSelectedPersonaId,
  setLastSelectedPersonaId,
} from "utilities/lastSelectedPersonaId";

import PersonaSelector from "components/PersonaSelector";
import IncognitoToggle from "components/IncognitoToggle";
import MessageInput from "components/MessageInput";
import MessageHistory from "components/MessageHistory";
import WelcomeMessage from "components/WelcomeMessage/WelcomeMessage";
import ScrollToBottomButton from "components/ScrollToBottomButton";

import useChatStream from "services/cortevaChain/hooks/useChatStream";
import useThreadsQuery from "services/dbApi/hooks/useThreadsQuery";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";
import useGetAccessToken from "components/Login/useGetAccessToken";
import { useStickToBottom } from "hooks/useStickToBottom";
import { useTaskWriteInFlight } from "hooks/tasks/useTaskWriteInFlight";

// Resolves the initial persona by looking up the last-selected id in
// localStorage and falling back to DEFAULT_PERSONA when nothing is stored or
// the stored persona has been deactivated.
function getInitialPersona(): types.Persona {
  const lastId = loadLastSelectedPersonaId();
  if (lastId === null) return types.DEFAULT_PERSONA;
  const persona = types.getPersonaById(lastId);
  return persona.active ? persona : types.DEFAULT_PERSONA;
}

export default function IncognitoChat() {
  const [messages, setMessages] = React.useState<types.Message[]>([]);
  const [userInput, setUserInput] = React.useState("");

  // Persona is plain client-side state. Persisted via the shared
  // `selectedPersonaId` localStorage key so the choice round-trips with
  // NewChat / Chat across reloads.
  const [selectedPersona, setSelectedPersonaState] =
    React.useState<types.Persona>(getInitialPersona);
  const setSelectedPersona = React.useCallback((newPersona: types.Persona) => {
    setSelectedPersonaState(newPersona);
    setLastSelectedPersonaId(newPersona.id);
  }, []);

  // If the current persona isn't allowed in incognito, fall back to the
  // default at render time without overwriting the stored selection.
  const incognitoPersonas = types.getIncognitoPersonas();
  const persona =
    selectedPersona.allowIncognito === false
      ? types.DEFAULT_PERSONA
      : selectedPersona;

  const navigate = useNavigate();
  const getAccessToken = useGetAccessToken();

  const chatWindowRef = React.useRef<null | HTMLDivElement>(null);
  // Follow/pause auto-scroll for streaming responses.
  const { isPinned, scrollToBottom, contentRef } = useStickToBottom({
    containerRef: chatWindowRef,
    messages,
  });

  // Restore the aborted user message into the draft so the user can edit it
  // and resend. useChatStream removes the user message and any partial
  // assistant response from history when the request is aborted.
  const handleStreamAbort = React.useCallback((userMessageContent: string) => {
    setUserInput(userMessageContent);
  }, []);

  // Exit incognito mode on Escape key.
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        navigate("/");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  const streamState = useChatStream({
    chatThreadId: "",
    persona,
    messages,
    setMessages,
    getAccessToken,
    onAbort: handleStreamAbort,
    incognito: true,
  });

  if (streamState.status === "error") {
    console.error(streamState.error);
  }

  const { refetch: refetchThreads } = useThreadsQuery();
  const { refetch: refetchTasks } = useTasksQuery();
  const taskWriteInFlight = useTaskWriteInFlight();

  // Refetch threads and tasks on tab focus to bring in any updates made by other
  // tabs. Incognito renders no sidebar of its own but shares the threads/tasks
  // cache, so this keeps the lists fresh for an intra-tab nav out of /incognito.
  // Skipped while a chat completion stream is in-progress to avoid clobbering
  // the React Query cache. The tasks refetch is additionally skipped while a task
  // write is in flight: the app-level poller runs on this route too and PATCHes
  // status and claims terminals for jobs in ordinary chats, so a focus GET here
  // can land with pre-commit server state and overwrite a just-committed row
  // (ADR 056). Incognito itself never registers a task (ADR 059).
  React.useEffect(() => {
    const handleFocus = () => {
      if (streamState.status === "loading") return;
      refetchThreads();
      if (taskWriteInFlight) return;
      refetchTasks();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [streamState.status, refetchThreads, refetchTasks, taskWriteInFlight]);

  const handleUserMessage = React.useCallback(
    (message: types.UserMessage) => {
      if (streamState.status === "loading") return;
      setMessages((prev) => [...prev, message]);
    },
    [streamState.status]
  );

  const searchEnabled = !persona.isInternal;

  const body =
    messages.length > 0 ? (
      <MessageHistory
        messages={messages}
        isStreaming={streamState.status === "loading"}
        contentRef={contentRef}
        incognito
      />
    ) : (
      <WelcomeMessage persona={persona} incognito />
    );

  const lastAssistantWithUsage = messages.findLast(
    (m): m is types.AssistantResponseMessage =>
      types.isAssistantResponseMessage(m) && m.tokenUsage !== undefined
  );
  const lastTokenUsage = lastAssistantWithUsage?.tokenUsage;
  const compactionFailed = lastAssistantWithUsage?.compactionFailed ?? false;

  return (
    <div className={styles.incognitoContainer}>
      <div className={styles.decorGradients} />
      {/* Header — above the dashed border */}
      <div className={styles.chatHeader}>
        <PersonaSelector
          personas={incognitoPersonas}
          selectedPersona={persona}
          setSelectedPersona={setSelectedPersona}
        />
        <IncognitoToggle />
      </div>
      <main className={`${styles.chat} ${styles.incognitoBorder}`}>
        {/* Body */}
        <div className={styles.chatBody} ref={chatWindowRef}>
          {body}
        </div>
        {!isPinned && messages.length > 0 && (
          <ScrollToBottomButton onClick={scrollToBottom} />
        )}

        {/* Sticky footer */}
        <div className={styles.chatFooter}>
          <MessageInput
            persona={persona}
            userInput={userInput}
            setUserInput={setUserInput}
            disabled={streamState.status === "loading"}
            onStop={streamState.abort}
            onSend={handleUserMessage}
            lastTokenUsage={lastTokenUsage}
            compactionFailed={compactionFailed}
            uploadDisabled={true}
            toolAvailability={{
              searchEnabled,
              jupyterHubEnabled: false,
            }}
          />
        </div>
      </main>
    </div>
  );
}
